import { Link } from "react-router-dom";
import { Mail, MessageCircle, Shield, Trash2, HelpCircle, Flag } from "lucide-react";

const SUPPORT_EMAIL = "soporte@cadii.app";

const FAQS = [
  {
    icon: HelpCircle,
    q: "¿Cómo creo mi cuenta?",
    a: "Descarga CADii, abre la app y toca 'Comenzar'. Puedes registrarte con tu correo electrónico o con Sign in with Apple / Google. Al finalizar, ingresa tu handicap y tu club (opcional) para completar tu perfil.",
  },
  {
    icon: Flag,
    q: "¿Cómo registro una ronda?",
    a: "Desde el Home toca 'Nueva ronda', elige el formato (GCC, Match Play, Nassau, Skins u otros), agrega jugadores desde tu lista de favoritos y comienza a capturar tu score hoyo por hoyo. La app calcula automáticamente handicap, apuestas y ganadores.",
  },
  {
    icon: MessageCircle,
    q: "¿Las apuestas involucran dinero real?",
    a: "No. CADii es un marcador digital para juegos amistosos entre golfistas. La app no procesa pagos ni actúa como intermediario financiero — solo lleva la cuenta de puntos y unidades para que sepas cuánto le toca pagar a quién al terminar la ronda.",
  },
  {
    icon: Shield,
    q: "¿Cómo cuidan mi información?",
    a: "Guardamos tus datos con conexiones cifradas (HTTPS) y las contraseñas con hash bcrypt. No vendemos tu información personal a terceros. Puedes leer todos los detalles en nuestra Política de Privacidad.",
  },
  {
    icon: Trash2,
    q: "¿Cómo elimino mi cuenta?",
    a: "Desde la app: Perfil → Configuración → Eliminar cuenta. La eliminación es permanente y borra tus rondas, tu handicap y tu información personal. Si iniciaste sesión con Apple, también se revocan las credenciales asociadas.",
  },
  {
    icon: HelpCircle,
    q: "Encontré un bug o tengo una idea",
    a: `Escríbenos a ${SUPPORT_EMAIL} con una descripción del problema (o de tu idea) y, si es posible, una captura de pantalla. Respondemos en menos de 48 horas hábiles.`,
  },
];

export default function Support() {
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="support-page">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="text-[#1B4D3E] font-semibold text-sm hover:underline" data-testid="support-home-link">
          ← Volver
        </Link>

        <div className="mt-6">
          <h1 className="text-3xl sm:text-4xl font-black text-[#102E32]">Centro de soporte</h1>
          <p className="text-base text-[#475569] mt-3 leading-relaxed">
            Estamos para ayudarte a sacarle el máximo a CADii. Aquí encontrarás respuestas rápidas
            y las formas de contactarnos.
          </p>
        </div>

        {/* Contact card */}
        <div className="mt-8 rounded-2xl bg-white border border-[#E5E7EB] shadow-sm p-6" data-testid="support-contact-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-[#1B4D3E]/10 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#1B4D3E]" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#64748B]">Contacto directo</p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-lg font-bold text-[#102E32] hover:text-[#1B4D3E]"
                data-testid="support-email-link"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <p className="text-sm text-[#475569]">
            Escríbenos con tu duda, problema o sugerencia. Respondemos en menos de 48 horas hábiles.
            Si es una falla, incluye el modelo de tu dispositivo y una captura de pantalla si puedes.
          </p>
        </div>

        {/* FAQs */}
        <div className="mt-10">
          <h2 className="text-xl font-black text-[#102E32] mb-4">Preguntas frecuentes</h2>
          <div className="space-y-3">
            {FAQS.map((f, idx) => {
              const Icon = f.icon;
              return (
                <details
                  key={idx}
                  className="group rounded-xl bg-white border border-[#E5E7EB] p-5 open:shadow-sm transition-shadow"
                  data-testid={`support-faq-${idx}`}
                >
                  <summary className="flex items-center gap-3 cursor-pointer list-none">
                    <div className="w-9 h-9 rounded-lg bg-[#1B4D3E]/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#1B4D3E]" />
                    </div>
                    <span className="font-bold text-[#102E32] flex-1">{f.q}</span>
                    <span className="text-[#94A3B8] group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <p className="text-[#475569] leading-relaxed mt-3 pl-12">{f.a}</p>
                </details>
              );
            })}
          </div>
        </div>

        {/* Legal links */}
        <div className="mt-10 flex flex-wrap gap-4 text-sm text-[#64748B]">
          <Link to="/privacy" className="hover:text-[#1B4D3E] hover:underline" data-testid="support-privacy-link">
            Privacy Policy
          </Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-[#1B4D3E] hover:underline" data-testid="support-terms-link">
            Terms of Service
          </Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-[#1B4D3E] hover:underline" data-testid="support-contact-link">
            Contact
          </Link>
        </div>

        <div className="mt-8 pt-6 border-t border-[#E5E7EB]" data-testid="support-legal-entity">
          <p className="text-xs text-[#64748B] leading-relaxed">
            CADii es operado por <strong className="text-[#102E32]">Multigoods Inmuebles, S. de R.L. de C.V.</strong>
            <br />
            CADii is operated by <strong className="text-[#102E32]">Multigoods Inmuebles, S. de R.L. de C.V.</strong>
          </p>
        </div>

        <p className="mt-6 text-xs text-[#94A3B8]">CADii · Golf, competencias y comunidad · Hecho con orgullo en México.</p>
      </div>
    </div>
  );
}
